import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { apiGet, apiPut, apiPost, apiDelete } from '../lib/auth'

export interface Notification {
  id: string
  type: string
  severity: 'critical' | 'warning' | 'info'
  title: string
  message: string
  deviceId?: string
  deviceName?: string
  timestamp: string
  read: boolean
}

export interface NotificationPreferences {
  grid_outage: boolean
  battery_low: boolean
  battery_critical: boolean
  system_fault: boolean
  device_offline: boolean
  device_online: boolean
  quietHoursEnabled: boolean
  quietHoursStart: string
  quietHoursEnd: string
}

const defaultPrefs: NotificationPreferences = {
  grid_outage: true,
  battery_low: true,
  battery_critical: true,
  system_fault: true,
  device_offline: true,
  device_online: false,
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '07:00',
}

const EVENT_TYPES = [
  'grid_outage', 'battery_low', 'battery_critical',
  'system_fault', 'device_offline', 'device_online',
] as const

interface NotificationState {
  notifications: Notification[]
  preferences: NotificationPreferences
  synced: boolean
  addNotification: (n: Omit<Notification, 'id' | 'read' | 'timestamp'>) => void
  markRead: (id: string) => void
  markAllRead: () => void
  clearAll: () => void
  updatePreferences: (prefs: Partial<NotificationPreferences>) => void
  syncFromServer: () => Promise<void>
  saveToServer: () => Promise<void>
  registerPushToken: (platform: string, token: string) => Promise<void>
  unregisterPushToken: (token: string) => Promise<void>
  unreadCount: () => number
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      preferences: defaultPrefs,
      synced: false,

      addNotification: (n) =>
        set((state) => ({
          notifications: [
            {
              ...n,
              id: crypto.randomUUID(),
              read: false,
              timestamp: new Date().toISOString(),
            },
            ...state.notifications,
          ].slice(0, 50),
        })),

      markRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n,
          ),
        })),

      markAllRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
        })),

      clearAll: () => set({ notifications: [] }),

      updatePreferences: (prefs) => {
        set((state) => ({
          preferences: { ...state.preferences, ...prefs },
        }))
        get().saveToServer().catch(() => {})
      },

      syncFromServer: async () => {
        try {
          const [prefsRes, qhRes] = await Promise.all([
            apiGet<{ preferences: { event_type: string; enabled: boolean }[] }>('/api/v1/notifications/preferences'),
            apiGet<{ enabled: boolean; start_time: string; end_time: string; timezone: string }>('/api/v1/notifications/quiet-hours'),
          ])

          const prefs = { ...defaultPrefs }
          for (const p of prefsRes.preferences ?? []) {
            if (p.event_type in prefs) {
              ;(prefs as Record<string, unknown>)[p.event_type] = p.enabled
            }
          }
          prefs.quietHoursEnabled = qhRes.enabled
          prefs.quietHoursStart = qhRes.start_time?.slice(0, 5) ?? '22:00'
          prefs.quietHoursEnd = qhRes.end_time?.slice(0, 5) ?? '07:00'

          set({ preferences: prefs, synced: true })
        } catch {
          // Offline or unauthenticated — use local prefs
        }
      },

      saveToServer: async () => {
        const { preferences } = get()
        try {
          const prefPayload = EVENT_TYPES.map((t) => ({
            event_type: t,
            enabled: preferences[t],
          }))
          await Promise.all([
            apiPut('/api/v1/notifications/preferences', { preferences: prefPayload }),
            apiPut('/api/v1/notifications/quiet-hours', {
              enabled: preferences.quietHoursEnabled,
              start_time: preferences.quietHoursStart + ':00',
              end_time: preferences.quietHoursEnd + ':00',
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
          ])
        } catch {
          // Offline — changes will be synced on next load
        }
      },

      registerPushToken: async (platform, token) => {
        await apiPost('/api/v1/notifications/push-token', { platform, token })
      },

      unregisterPushToken: async (token) => {
        await apiDelete('/api/v1/notifications/push-token', { token })
      },

      unreadCount: () => get().notifications.filter((n) => !n.read).length,
    }),
    {
      name: 'esi-notifications',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        notifications: state.notifications,
        preferences: state.preferences,
      }),
    },
  ),
)
